import type { EnrichmentChange, EnrichmentEvent, TaskActivity } from '@portfolio-pm/shared';

type AuditItem =
  | { kind: 'activity'; timestamp: number; activity: TaskActivity }
  | { kind: 'enrichment'; timestamp: number; event: EnrichmentEvent };

export function TaskAuditHistory({
  activities,
  enrichmentEvents,
}: {
  activities: TaskActivity[];
  enrichmentEvents: EnrichmentEvent[];
}): JSX.Element {
  const items: AuditItem[] = [
    ...activities
      .filter((activity) => activity.type !== 'comment')
      .map(
        (activity): AuditItem => ({
          kind: 'activity',
          timestamp: activity.createdAt,
          activity,
        }),
      ),
    ...enrichmentEvents.map(
      (event): AuditItem => ({ kind: 'enrichment', timestamp: event.createdAt, event }),
    ),
  ].sort((a, b) => b.timestamp - a.timestamp);

  return (
    <section className="pane-divider-t" data-testid="task-audit-history">
      <div className="flex min-h-10 items-center px-2">
        <h3 className="text-sm font-medium text-[color:var(--app-text)]">Audit history</h3>
        <span className="ml-auto text-xs text-[color:var(--app-text-soft)]">read-only</span>
      </div>
      {items.length === 0 ? (
        <p className="pane-divider-t px-3 py-4 text-xs text-[color:var(--app-text-soft)]">
          No recorded changes for this task yet.
        </p>
      ) : (
        <ol className="pane-divider-t pane-divide-y">
          {items.map((item) =>
            item.kind === 'activity' ? (
              <li key={`activity-${item.activity.id}`} className="px-3 py-3">
                <p className="text-sm leading-5 text-[color:var(--app-text)]">
                  {item.activity.message}
                </p>
                <p className="mt-1 text-xs text-[color:var(--app-text-soft)]">
                  PPM · {humanize(item.activity.type)} · {formatTimestamp(item.timestamp)}
                </p>
              </li>
            ) : (
              <li key={`enrichment-${item.event.id}`} className="px-3 py-3">
                <div className="flex items-start justify-between gap-3">
                  <p className="text-sm leading-5 text-[color:var(--app-text)]">
                    Planning change {humanize(item.event.action)}
                  </p>
                  {item.event.confirmationId && (
                    <span className="shrink-0 font-mono text-[10px] text-[color:var(--app-text-soft)]">
                      {item.event.confirmationId}
                    </span>
                  )}
                </div>
                {item.event.changes.length > 0 && (
                  <ul className="mt-1.5 space-y-1 border-l-2 border-[color:var(--app-border)] pl-2 text-xs leading-5">
                    {item.event.changes.map((change, index) => (
                      <li key={`${change.field}-${index}`} className="text-[color:var(--app-text)]">
                        <span className="font-medium">{humanize(change.field)}</span>{' '}
                        <span className="text-[color:var(--app-text-soft)]">
                          {formatChange(change)}
                        </span>
                      </li>
                    ))}
                  </ul>
                )}
                <p className="mt-1.5 text-xs text-[color:var(--app-text-soft)]">
                  {item.event.actor}
                  {item.event.tool ? ` via ${item.event.tool}` : ''} ·{' '}
                  {formatTimestamp(item.timestamp)}
                </p>
              </li>
            ),
          )}
        </ol>
      )}
    </section>
  );
}

function formatChange(change: EnrichmentChange): string {
  if (change.valueState) return `marked ${humanize(change.valueState)}`;
  return `${formatValue(change.before)} → ${formatValue(change.after)}`;
}

function formatValue(value: unknown): string {
  if (value === undefined || value === null || value === '') return 'not set';
  if (Array.isArray(value)) return value.length ? value.map(formatValue).join(', ') : 'empty';
  if (typeof value === 'object') return JSON.stringify(value);
  return humanize(String(value));
}

function formatTimestamp(value: number): string {
  return new Intl.DateTimeFormat(undefined, {
    dateStyle: 'medium',
    timeStyle: 'short',
  }).format(new Date(value));
}

function humanize(value: string): string {
  return value.replaceAll('_', ' ');
}
